import { Button, Typography } from '@mui/material';
import { FormikConfig, useFormik } from 'formik';
import * as Yup from 'yup';
import { EditorState } from 'draft-js';

import { useModals } from 'src/hooks';
import Box from 'src/components/Box';
import { OpportunityApplicationQuestion, OpportunityApplicationQuestionAnswer } from 'src/types/resources/Opportunity';
import TextEditor from 'src/components/TextEditor';
import { MAX_APPLY_MESSAGE_TEXTAREA_LENGTH, ValidationError } from 'src/enums/Validation';
import { getRawTextEditorValue, isEmptyTextEditor, convertToHtmlFormat } from 'src/utils/textEditor';

import { opportunityApplyFormStyles } from './styles';

type TalentOpportunityApplyFormValues = {
  answers: Record<ID, EditorState>;
};

type TalentOpportunityApplyFormProps = {
  handleApply: (data: OpportunityApplicationQuestionAnswer[]) => void;
  applicationQuestions: OpportunityApplicationQuestion[];
};

const TalentOpportunityApplyForm: React.FC<TalentOpportunityApplyFormProps> = ({
  applicationQuestions = [],
  handleApply,
}) => {
  const { hideModal } = useModals();

  // Create initial values for formik
  const initialValues: TalentOpportunityApplyFormValues = {
    answers: applicationQuestions.reduce((acc, question) => {
      return { ...acc, [question.id]: EditorState.createEmpty() };
    }, {} as Record<ID, EditorState>),
  };

  const validationSchema = Yup.object({
    answers: Yup.object(
      applicationQuestions.reduce(
        (acc, question) => {
          return {
            ...acc,
            [question.id]: Yup.mixed<EditorState>()
              .test({
                test: (state: EditorState) => !isEmptyTextEditor(state),
                message: ValidationError.default,
              })
              .test({
                test: (state: EditorState) => getRawTextEditorValue(state).length <= MAX_APPLY_MESSAGE_TEXTAREA_LENGTH,
                message: 'You cannot use more then 2,500 characters',
              }),
          };
        },
        //  {} as Record<number, typeof MixedSchema<EditorState>>
        {},
      ),
    ),
  });

  const handleSubmit: FormikConfig<TalentOpportunityApplyFormValues>['onSubmit'] = (formData, { setSubmitting }) => {
    const questionAnswers: OpportunityApplicationQuestionAnswer[] = Object.entries(formData.answers).map(
      ([questionId, answer]) => ({
        opportunityQuestionId: Number(questionId),
        answer: convertToHtmlFormat(answer),
      }),
    );

    handleApply(questionAnswers);
    setSubmitting(false); // manually reset
  };

  const { values, errors, submitForm, touched, setFieldValue, isSubmitting } =
    useFormik<TalentOpportunityApplyFormValues>({
      initialValues,
      validationSchema,
      onSubmit: handleSubmit,
      validateOnChange: true,
    });

  return (
    <Box sx={opportunityApplyFormStyles.wrapper}>
      <Typography variant="h3">Confirm That You Are the Ideal Talent</Typography>
      <Typography>
        Note to applicants: Before applying, review the opportunity details and ensure you meet the qualifications. Your
        responses serve as a crucial prescreen, so highlight relevant skills and experience that align with the
        opportunity. The Opportunity Manager can view your profile—avoid copying existing information and providing the
        same answer for each section.
      </Typography>
      {applicationQuestions.map(question => (
        <TextEditor
          key={question.id}
          labelSx={{ whiteSpace: 'normal' }}
          label={question.question}
          isCounterVisible
          maxValue={MAX_APPLY_MESSAGE_TEXTAREA_LENGTH}
          value={values.answers[question.id] ?? EditorState.createEmpty()}
          onChange={newState => setFieldValue(`answers.${question.id}`, newState)}
          error={touched.answers?.[question.id] && errors.answers?.[question.id]}
          usePredefinedMinHeight
          wrapperSx={{
            margin: 0,
          }}
        />
      ))}
      <Box sx={opportunityApplyFormStyles.buttons}>
        <Button variant="contained" onClick={submitForm} disabled={isSubmitting}>
          Apply
        </Button>
        <Button variant="outlined" onClick={hideModal}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default TalentOpportunityApplyForm;
